import { useEffect, useRef, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { loadGoogleMaps } from 'components/shared/googleMapsLoader';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';
import { useObservability } from 'helpers/observability';

interface PdpLocationMapProps {
  latitude: number;
  longitude: number;
}

export const PdpLocationMap = ({ latitude, longitude }: PdpLocationMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const { trackError } = useObservability();
  const { inView } = useInViewAfterLoad({
    ref: containerRef,
    rootMargin: '256px',
    triggerOnce: true,
    fallbackInView: true,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!inView || !container) return undefined;

    // Keep each effect's DOM separate when React replays it or the property changes.
    const canvas = document.createElement('div');
    canvas.className = 'absolute inset-0';
    container.appendChild(canvas);
    let cancelled = false;
    let cleanup = () => {};
    let errorPhase: 'sdk_load' | 'map_initialization' = 'sdk_load';

    loadGoogleMaps()
      .then((api) => {
        if (cancelled) return undefined;
        errorPhase = 'map_initialization';
        const map = new api.maps.Map(canvas, {
          center: { lat: latitude, lng: longitude },
          zoom: 13,
          mapTypeId: api.maps.MapTypeId.ROADMAP,
          disableDefaultUI: true,
          clickableIcons: false,
          scrollwheel: false,
          gestureHandling: 'cooperative',
          // Zoom is handled by the custom pill control rendered below.
          zoomControl: false,
        });
        mapRef.current = map;
        setMapReady(true);
        let pin: google.maps.OverlayView | null = null;
        cleanup = () => {
          mapRef.current = null;
          setMapReady(false);
          pin?.setMap(null);
          api.maps.event.clearInstanceListeners(map);
          map.unbindAll();
        };

        const position = new api.maps.LatLng(latitude, longitude);
        class PropertyPin extends api.maps.OverlayView {
          private readonly image = document.createElement('img');

          override onAdd() {
            this.image.src =
              'https://images.opendoor.com/source/s3/imgdrop-production/2024-07-1720722843921-87270.png?preset=square-2048';
            this.image.alt = 'Property location';
            this.image.className = 'absolute w-5 max-w-none -translate-x-1/2 -translate-y-full';
            this.getPanes()?.overlayMouseTarget.appendChild(this.image);
          }

          override draw() {
            const point = this.getProjection().fromLatLngToDivPixel(position);
            if (!point) return;
            this.image.style.left = `${point.x}px`;
            this.image.style.top = `${point.y}px`;
          }

          override onRemove() {
            this.image.remove();
          }
        }
        pin = new PropertyPin();
        pin.setMap(map);
        setFailed(false);
        return undefined;
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        cleanup();
        canvas.remove();
        setFailed(true);
        let errorFailureKind = 'non_error';
        if (error instanceof Error) errorFailureKind = 'error';
        else if (error instanceof Event && error.type === 'error') {
          errorFailureKind = 'script_error_event';
        }
        trackError(
          'marketplace-property-google-map-initialization-failed',
          error instanceof Error
            ? error
            : new Error('Property map failed with a non-Error rejection'),
          {
            errorComponent: 'pdp_location_map',
            errorPhase,
            errorFailureKind,
          },
        );
      });

    return () => {
      cancelled = true;
      cleanup();
      canvas.remove();
    };
  }, [inView, latitude, longitude, trackError]);

  const zoomBy = (delta: number) => {
    const map = mapRef.current;
    if (!map) return;
    map.setZoom((map.getZoom() ?? 13) + delta);
  };

  return (
    <Box
      ref={containerRef}
      as="section"
      aria-label="Property location map"
      className="relative h-full w-full bg-od-bg-secondary"
    >
      {mapReady && !failed && (
        <Col
          role="group"
          aria-label="Map zoom"
          className="absolute right-3 top-3 z-[1] w-8 items-center gap-2 rounded-full bg-od-bg-primary p-1"
        >
          <button
            type="button"
            aria-label="Zoom in"
            onClick={() => zoomBy(1)}
            className="flex size-6 items-center justify-center text-od-content-primary"
          >
            <IconPlus size={18} aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            onClick={() => zoomBy(-1)}
            className="flex size-6 items-center justify-center text-od-content-primary"
          >
            <IconMinus size={18} aria-hidden />
          </button>
        </Col>
      )}
      {failed && (
        <Col className="h-full items-center justify-center gap-2 p-4">
          <Text>Unable to load the map.</Text>
          <Link
            analyticsName="pdp-location-google-maps"
            className="text-od-content-accent-brand underline"
            href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open location in Google Maps
          </Link>
        </Col>
      )}
    </Box>
  );
};
